import { useCallback, useEffect, useRef, useState } from "react";
import { fetchStockBuyRecommendationFilters } from "../api/stockBuyRecommendationFilters";
import { BUY_REC_FILTER_ALL } from "../utils/stockBuyRecommendationTabs";

function filtersEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((item, i) => {
    const other = b[i];
    return (
      item.id === other.id &&
      item.name === other.name &&
      item.yahooUrl === other.yahooUrl &&
      (item.stockCount ?? 0) === (other.stockCount ?? 0) &&
      (item.yahooLastRefreshedAt || null) === (other.yahooLastRefreshedAt || null)
    );
  });
}

function countsEqual(a, b) {
  if (a === b) return true;
  const keysA = Object.keys(a || {});
  const keysB = Object.keys(b || {});
  if (keysA.length !== keysB.length) return false;
  return keysA.every((k) => a[k] === b[k]);
}

/**
 * Load saved buy-recommendation filters once per account; explicit reload via returned function.
 */
export function useStockBuyRecFilters({ accountId, serverUrl, stockSession }) {
  const [savedFilters, setSavedFilters] = useState([]);
  const [aggregateCount, setAggregateCount] = useState(0);
  const [counts, setCounts] = useState({});
  const inflightRef = useRef(null);
  const loadedAccountRef = useRef("");

  const reloadFilters = useCallback(async () => {
    if (!accountId) return;
    if (inflightRef.current) return inflightRef.current;

    inflightRef.current = (async () => {
      try {
        const { savedFilters: nextFilters, aggregateCount: nextAgg } =
          await fetchStockBuyRecommendationFilters(serverUrl, stockSession);
        const nextCounts = { [BUY_REC_FILTER_ALL]: nextAgg };
        nextFilters.forEach((f) => {
          nextCounts[f.id] = f.stockCount ?? 0;
        });

        setSavedFilters((prev) => (filtersEqual(prev, nextFilters) ? prev : nextFilters));
        setAggregateCount((prev) => (prev === nextAgg ? prev : nextAgg));
        setCounts((prev) => (countsEqual(prev, nextCounts) ? prev : nextCounts));
      } finally {
        inflightRef.current = null;
      }
    })();

    return inflightRef.current;
  }, [accountId, serverUrl, stockSession]);

  useEffect(() => {
    if (!accountId) return;
    if (loadedAccountRef.current === accountId) return;
    loadedAccountRef.current = accountId;
    reloadFilters();
  }, [accountId, reloadFilters]);

  const patchCount = useCallback(({ filterId, count }) => {
    setCounts((prev) => {
      if (prev[filterId] === count) return prev;
      return { ...prev, [filterId]: count };
    });
    if (filterId === BUY_REC_FILTER_ALL) {
      setAggregateCount((prev) => (prev === count ? prev : count));
    }
  }, []);

  return {
    savedFilters,
    aggregateCount,
    counts,
    reloadFilters,
    patchCount,
  };
}
